/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, DoCheck, Input, ViewChild, ViewContainerRef } from '@angular/core';
import { ControlOn, AppFormControl } from '@src/app/shared/form/app-form-control';
import { Field } from '@src/app/shared/field/Field';
import { FormControlInterface } from '@src/app/shared/form/form-control-interface';
import { NgClass } from '@angular/common';
import { FormErrorComponent } from '@src/app/shared/form/form-error/form-error.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { DateHelper } from '@src/app/shared/dateHelper';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { ErrorStateMatcher } from '@angular/material/core';
import { IRISH_DATE_PROVIDERS, typedDateWasRejected } from '@src/app/shared/form/irish-date-adapter';

export class DateField extends Field {
    formatValue(val: string) {
        return DateHelper.localDate(val);
    }

    setDateValue(dt: Date) {
        this.control.setValue(dt ? DateHelper.isoFromCalendarDate(dt) : '');
    }
}
@Component({
    selector: 'app-form-date',
    templateUrl: './form-date.component.html',
    styleUrls: ['./form-date.component.scss'],
    imports: [MatFormFieldModule, MatInputModule, FormsModule, MatDatepickerModule,
        ReactiveFormsModule, FormErrorComponent, NgClass, MatTooltipModule, MatIconModule],
    providers: IRISH_DATE_PROVIDERS
})
export class FormDateComponent implements FormControlInterface, DoCheck {

    @Input() control: AppFormControl;
    @Input() on: ControlOn = 'form';

    /** The picker input is not bound to the control, so mat-form-field cannot see its errors
     *  unless told where to look. */
    readonly showControlErrors: ErrorStateMatcher = {
        isErrorState: () => this.control.invalid && (this.control.touched || this.control.dirty)
    };

    @ViewChild(MatInput) pickerInput: MatInput;

    /** MatInput only re-checks its error state itself when it has a bound form control. */
    ngDoCheck() {
        this.pickerInput?.updateErrorState();
    }

    private shownIsoDate: string;
    private shownCalendarDate: Date | null = null;

    public static make(heading: string, value: string, options: Partial<Field> = {}) : DateField{
        const field: DateField = new DateField({ cellOpts: { heading }, value, type: 'date' }, options);
        field.formControlFactory = FormDateComponent.createComponent;
        return field;
    }

    public static createComponent(vcr: ViewContainerRef, ctl: AppFormControl, on: ControlOn) {
        const componentRef = vcr.createComponent(FormDateComponent);
        componentRef.instance.control = ctl;
        componentRef.instance.on = on;
        return componentRef;
    }

    /* Parochial but efficient */
    public formatToLocal(yyyyMMdd: string) {
        if (yyyyMMdd) {
            return yyyyMMdd.substring(8, 10) + '/' + yyyyMMdd.substring(5, 7) + '/' + yyyyMMdd.substring(0, 4);
        }
        return '';
    }

    /** Returns the same Date instance until the control value changes - a fresh one on every
     *  change detection would rewrite the input text while the user is still typing. */
    public calendarDate(): Date | null {
        if (this.control.value !== this.shownIsoDate) {
            this.shownIsoDate = this.control.value;
            this.shownCalendarDate = DateHelper.calendarDateFromIso(this.shownIsoDate);
        }
        return this.shownCalendarDate;
    }

    public isEditable() {
        return !this.control.field.readonly && !this.control.field.disable && !this.control.disabled;
    }

    /** The control keeps its yyyy-mm-dd string - the picker's Date is only ever a local calendar
     *  day, and is converted back with local getters so no time zone can shift it. */
    public applyChosenDate(change: MatDatepickerInputEvent<Date>) {
        if (typedDateWasRejected(change)) {
            change.target.value = this.calendarDate();
            return;
        }
        this.control.setValue(change.value ? DateHelper.isoFromCalendarDate(change.value) : '');
        this.control.markAsDirty();
        this.control.markAsTouched();
    }

}
